import { Text } from '@mantine/core'
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'

import { easeOutSoft } from '~/boggle/ui/atoms/RampingNumberText'
import { RUN_RESULTS_SCORE_RAMP_BASE_MS } from '~/boggle/ui/run/runResultsScoreRamp'

import styles from './FloatingScoreTag.module.css'

const DEFAULT_DURATION_MS = RUN_RESULTS_SCORE_RAMP_BASE_MS
const DEFAULT_FADE_OUT_MS = 180
const DEFAULT_OFFSET_X = 14
const DEFAULT_COLOR = '#f0c85a'
/** Hold the tag at full value for this long after it appears before starting the drain. */
const DEFAULT_PRE_DRAIN_DELAY_MS = 240

function clamp01(t: number) {
  return Math.min(1, Math.max(0, t))
}

/**
 * A small "+N" tag that anchors next to a DOM element (e.g. Tiles Cleared, a tier value)
 * and ramps its value down to zero, then fades out. Position polls via RAF so the tag
 * follows the anchor through layout transitions.
 *
 * The tag is purely visual; pair it with a `RampingNumberText` set to the new running
 * total (using the same `durationMs`) to get the synchronized count-up Score arcade feel.
 */
export function FloatingScoreTag({
  anchorRef,
  color = DEFAULT_COLOR,
  durationMs = DEFAULT_DURATION_MS,
  fadeOutMs = DEFAULT_FADE_OUT_MS,
  fz = 26,
  offsetX = DEFAULT_OFFSET_X,
  onComplete,
  preDrainDelayMs = DEFAULT_PRE_DRAIN_DELAY_MS,
  startValue,
}: {
  /** Element to anchor the tag to (positioned to the right of its bounding rect, vertically centered). */
  anchorRef: RefObject<HTMLElement | null>
  color?: string
  /** Drain duration (the time to count down from `startValue` to 0). */
  durationMs?: number
  /** Time to fade out after the value reaches zero. */
  fadeOutMs?: number
  /** Tag font size. */
  fz?: number
  /** Horizontal gap from the right edge of the anchor. */
  offsetX?: number
  /** Fires after fade-out is complete; used by the choreography hook to advance to the next tag. */
  onComplete?: () => void
  /** Hold at `startValue` for this long after appearing before the drain starts. */
  preDrainDelayMs?: number
  /** Starting value displayed in the tag (e.g. `+N` for badge points). Ramps down to 0. */
  startValue: number
}) {
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null)
  const [displayValue, setDisplayValue] = useState(startValue)
  const [isFadingOut, setIsFadingOut] = useState(false)
  const onCompleteRef = useRef(onComplete)
  useLayoutEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  // Poll anchor position each frame so the tag follows ongoing layout transitions.
  useEffect(() => {
    let raf: number | null = null
    const update = () => {
      const el = anchorRef.current
      if (el) {
        const rect = el.getBoundingClientRect()
        setPosition({
          left: rect.right + offsetX,
          top: rect.top + rect.height / 2,
        })
      }
      raf = requestAnimationFrame(update)
    }
    raf = requestAnimationFrame(update)
    return () => {
      if (raf !== null) {
        cancelAnimationFrame(raf)
      }
    }
  }, [anchorRef, offsetX])

  // Lifecycle: appear at `startValue` → hold for `preDrainDelayMs` → drain over
  // `durationMs` (ease-out cubic) → brief hold → fade out → notify completion.
  useEffect(() => {
    setDisplayValue(startValue)
    setIsFadingOut(false)
    let raf: number | null = null
    let preDelayTimer: number | null = null
    let fadeTimer: number | null = null
    let completeTimer: number | null = null

    const beginDrain = () => {
      const startTime =
        typeof performance !== 'undefined' ? performance.now() : Date.now()

      const tick = (now: number) => {
        const elapsed = now - startTime
        const t = durationMs <= 0 ? 1 : clamp01(elapsed / durationMs)
        const eased = easeOutSoft(t)
        const next = startValue * (1 - eased)
        setDisplayValue(next)

        if (t < 1) {
          raf = requestAnimationFrame(tick)
          return
        }

        setDisplayValue(0)
        fadeTimer = window.setTimeout(() => {
          setIsFadingOut(true)
          completeTimer = window.setTimeout(() => {
            onCompleteRef.current?.()
          }, fadeOutMs)
        }, 80)
      }
      raf = requestAnimationFrame(tick)
    }

    if (preDrainDelayMs > 0) {
      preDelayTimer = window.setTimeout(beginDrain, preDrainDelayMs)
    } else {
      beginDrain()
    }

    return () => {
      if (raf !== null) {
        cancelAnimationFrame(raf)
      }
      if (preDelayTimer !== null) {
        window.clearTimeout(preDelayTimer)
      }
      if (fadeTimer !== null) {
        window.clearTimeout(fadeTimer)
      }
      if (completeTimer !== null) {
        window.clearTimeout(completeTimer)
      }
    }
  }, [durationMs, fadeOutMs, preDrainDelayMs, startValue])

  if (typeof document === 'undefined' || !position) {
    return null
  }

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        transform: 'translateY(-50%)',
        opacity: isFadingOut ? 0 : 1,
        pointerEvents: 'none',
        transition: `opacity ${fadeOutMs}ms ease-out`,
        zIndex: 100,
      }}
    >
      <div className={styles.enterPop}>
        <Text c={color} fz={fz} fw={700} style={{ lineHeight: 1 }}>
          +{Math.ceil(displayValue)}
        </Text>
      </div>
    </div>,
    document.body,
  )
}

/** Final drain phase for the pooled badge transfer tag (see `FloatingAccumulatingScoreTag`). */
export type FloatingAccumulatingScoreFinalDrain = {
  durationMs: number
  key: string
  preDrainDelayMs: number
}

/**
 * Single "+N" badge transfer tag: value jumps to each new cumulative sum as badges appear
 * (with a jiggle on each bump), then after all are tallied it holds once and drains to 0.
 */
export function FloatingAccumulatingScoreTag({
  anchorRef,
  color = DEFAULT_COLOR,
  cumulativeValue,
  fadeOutMs = DEFAULT_FADE_OUT_MS,
  finalDrain,
  fz = 26,
  jiggleNonce,
  offsetX = DEFAULT_OFFSET_X,
  onDrainComplete,
}: {
  anchorRef: RefObject<HTMLElement | null>
  color?: string
  cumulativeValue: number
  fadeOutMs?: number
  finalDrain: FloatingAccumulatingScoreFinalDrain | null
  fz?: number
  jiggleNonce: number
  offsetX?: number
  onDrainComplete?: () => void
}) {
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null)
  /** During final drain; `null` means show live `cumulativeValue`. */
  const [drainDisplay, setDrainDisplay] = useState<number | null>(null)
  const [isFadingOut, setIsFadingOut] = useState(false)

  const onDrainCompleteRef = useRef(onDrainComplete)
  useLayoutEffect(() => {
    onDrainCompleteRef.current = onDrainComplete
  }, [onDrainComplete])

  const cumulativeRef = useRef(cumulativeValue)
  cumulativeRef.current = cumulativeValue

  useEffect(() => {
    let raf: number | null = null
    const update = () => {
      const el = anchorRef.current
      if (el) {
        const rect = el.getBoundingClientRect()
        setPosition({
          left: rect.right + offsetX,
          top: rect.top + rect.height / 2,
        })
      }
      raf = requestAnimationFrame(update)
    }
    raf = requestAnimationFrame(update)
    return () => {
      if (raf !== null) {
        cancelAnimationFrame(raf)
      }
    }
  }, [anchorRef, offsetX])

  useEffect(() => {
    if (!finalDrain) {
      return
    }

    setDrainDisplay(null)
    setIsFadingOut(false)

    const { durationMs, preDrainDelayMs } = finalDrain
    const fadeMs = fadeOutMs

    let preDelayTimer: number | null = null
    let drainRaf: number | null = null
    let fadeTimer: number | null = null
    let completeTimer: number | null = null

    const beginDrain = () => {
      const from = Math.max(0, cumulativeRef.current)
      if (from <= 0 || durationMs <= 0) {
        setDrainDisplay(0)
        fadeTimer = window.setTimeout(() => {
          setIsFadingOut(true)
          completeTimer = window.setTimeout(() => {
            onDrainCompleteRef.current?.()
          }, fadeMs)
        }, 80)
        return
      }

      setDrainDisplay(from)
      const startTime =
        typeof performance !== 'undefined' ? performance.now() : Date.now()

      const tick = (now: number) => {
        const elapsed = now - startTime
        const t = durationMs <= 0 ? 1 : clamp01(elapsed / durationMs)
        const eased = easeOutSoft(t)
        const next = from * (1 - eased)
        setDrainDisplay(next)

        if (t < 1) {
          drainRaf = requestAnimationFrame(tick)
          return
        }

        setDrainDisplay(0)
        fadeTimer = window.setTimeout(() => {
          setIsFadingOut(true)
          completeTimer = window.setTimeout(() => {
            onDrainCompleteRef.current?.()
          }, fadeMs)
        }, 80)
      }
      drainRaf = requestAnimationFrame(tick)
    }

    if (preDrainDelayMs > 0) {
      preDelayTimer = window.setTimeout(beginDrain, preDrainDelayMs)
    } else {
      beginDrain()
    }

    return () => {
      if (drainRaf !== null) {
        cancelAnimationFrame(drainRaf)
      }
      if (preDelayTimer !== null) {
        window.clearTimeout(preDelayTimer)
      }
      if (fadeTimer !== null) {
        window.clearTimeout(fadeTimer)
      }
      if (completeTimer !== null) {
        window.clearTimeout(completeTimer)
      }
    }
  }, [
    fadeOutMs,
    finalDrain?.durationMs,
    finalDrain?.key,
    finalDrain?.preDrainDelayMs,
  ])

  if (typeof document === 'undefined' || !position) {
    return null
  }

  const showValue = drainDisplay !== null ? drainDisplay : cumulativeValue

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        transform: 'translateY(-50%)',
        opacity: isFadingOut ? 0 : 1,
        pointerEvents: 'none',
        transition: `opacity ${fadeOutMs}ms ease-out`,
        zIndex: 101,
      }}
    >
      <div key={jiggleNonce} className={styles.enterPop}>
        <Text c={color} fz={fz} fw={700} style={{ lineHeight: 1 }}>
          +{Math.ceil(showValue)}
        </Text>
      </div>
    </div>,
    document.body,
  )
}
